import { CreateFreeformRequest, UpdateFreeformRequest } from "../models/freeform.model";
import { InteractiveFormValue } from "../models/interactive-form.model";
import { InteractiveState, TimeWindow, requiresStart, requiresEnd, Interactive } from "../models/interactive.model";
import { CreatePollRequest, UpdatePollRequest } from "../models/poll.model";

function deriveTimeWindow(hasStart: boolean, hasEnd: boolean): TimeWindow {
    if (hasStart && hasEnd) return "START_AND_END";
    if (hasStart) return "START_ONLY";
    if (hasEnd) return "END_ONLY";
    return "NONE";
}

type Shared<A, B> = Pick<A, Extract<keyof A, keyof B>>;

// Shared fields for a create request (freeform or poll)
function toCreateBaseFields(
    value: InteractiveFormValue,
    targetState: InteractiveState
): Shared<CreateFreeformRequest, CreatePollRequest> {
    return {
        tenantId: value.tenantId,
        name: value.name,
        prompt: value.prompt,
        description: value.description,
        state: targetState === "PUBLISHED" ? "PUBLISHED" : "DRAFT",
        timeWindow: deriveTimeWindow(value.hasStartDate, value.hasEndDate),
        startDate: value.startDate,
        endDate: value.endDate
    };
}

// Shared fields for an update request (freeform or poll)
function toUpdateBaseFields(
    value: InteractiveFormValue,
    targetState: InteractiveState
): Shared<UpdateFreeformRequest, UpdatePollRequest> {
    return {
        name: value.name,
        prompt: value.prompt,
        description: value.description,
        state: targetState,
        timeWindow: deriveTimeWindow(value.hasStartDate, value.hasEndDate),
        startDate: value.startDate,
        endDate: value.endDate
    };
}

export function toInteractiveFormValue(response: Interactive): InteractiveFormValue {
    const freeform = response.type === "freeform" ? response : null;
    const poll = response.type === "poll" ? response : null;
    return {
        type: response.type,
        name: response.name,
        tenantId: response.tenantId,
        prompt: response.prompt ?? "",
        description: response.description ?? "",
        hasStartDate: requiresStart(response.timeWindow),
        hasEndDate: requiresEnd(response.timeWindow),
        startDate: response.startDate,
        endDate: response.endDate,

        // freeform-only form values
        allowsText: freeform?.allowsText ?? null,
        allowsAudio: freeform?.allowsAudio ?? null,
        allowsVideo: freeform?.allowsVideo ?? null,
        maxCharacterOverride: freeform?.maxCharacterOverride ?? null,
        maxDurationOverride: freeform?.maxDurationOverride ?? null,
        maxVideoDurationOverride: freeform?.maxVideoDurationOverride ?? null,

        // poll-only form values
        isMultiSelect: poll?.isMultiSelect ?? false,
        displayResultsOverridable: poll?.displayResultsOverridable ?? false,
        maxSelectionsAllowed: poll?.maxSelectionsAllowed ?? null,
        pollOptions: poll?.pollOptions.map(o => ({ id: o.id, text: o.text })) ?? []
    };
}

export function toDuplicateFormValue(response: Interactive): InteractiveFormValue {
    // Single-select polls can't be open-ended, so hasEndDate must stay true even though the
    // actual date is cleared below — otherwise the form would load with the disabled
    // "No end date" radio silently selected and no end date required.
    const isSingleSelectPoll = response.type === "poll" && !response.isMultiSelect;
    return {
        ...toInteractiveFormValue(response),
        hasStartDate: false,
        hasEndDate: isSingleSelectPoll,
        startDate: null,
        endDate: null,
        pollOptions: response.type === "poll" ? response.pollOptions.map(o => ({ id: null, text: o.text })) : []
    };
}

export function toCreateFreeformRequest(
    value: InteractiveFormValue,
    targetState: InteractiveState
): CreateFreeformRequest {
    return {
        ...toCreateBaseFields(value, targetState),
        allowsText: value.allowsText ?? false,
        allowsAudio: value.allowsAudio ?? false,
        allowsVideo: value.allowsVideo ?? false,
        maxCharacterOverride: value.maxCharacterOverride,
        maxDurationOverride: value.maxDurationOverride,
        maxVideoDurationOverride: value.maxVideoDurationOverride
    };
}

export function toUpdateFreeformRequest(
    value: InteractiveFormValue,
    targetState: InteractiveState
): UpdateFreeformRequest {
    return {
        ...toUpdateBaseFields(value, targetState),
        allowsText: value.allowsText ?? false,
        allowsAudio: value.allowsAudio ?? false,
        allowsVideo: value.allowsVideo ?? false,
        maxCharacterOverride: value.maxCharacterOverride,
        maxDurationOverride: value.maxDurationOverride,
        maxVideoDurationOverride: value.maxVideoDurationOverride
    };
}

export function toCreatePollRequest(value: InteractiveFormValue, targetState: InteractiveState): CreatePollRequest {
    return {
        ...toCreateBaseFields(value, targetState),
        isMultiSelect: value.isMultiSelect,
        allowsOtherOption: value.isMultiSelect, // listeners can submit a write-in option for all multi-select polls
        displayResultsOverridable: value.displayResultsOverridable,
        maxSelectionsAllowed: value.maxSelectionsAllowed,
        pollOptions: value.pollOptions.map(o => ({ text: o.text }))
    };
}

export function toUpdatePollRequest(value: InteractiveFormValue, targetState: InteractiveState): UpdatePollRequest {
    return {
        ...toUpdateBaseFields(value, targetState),
        isMultiSelect: value.isMultiSelect,
        allowsOtherOption: value.isMultiSelect, // listeners can submit a write-in option for all multi-select polls
        displayResultsOverridable: value.displayResultsOverridable,
        maxSelectionsAllowed: value.maxSelectionsAllowed,
        pollOptions: value.pollOptions.map(o => (o.id ? { id: o.id, text: o.text } : { text: o.text }))
    };
}
